import { useCallback, useContext, useMemo } from "react";
import { useSelector } from "react-redux";

import { WeglotContext } from "@stadium/services/weglot/provider";

import { useConfig } from "@stadium/hooks/lib/use-config";
import useLockerUrlConfig from "@hooks/use-locker-url-config";
import useCountryChange from "@hooks/use-country-change";
import { useLanguageChange } from "@hooks/use-language-change";
import useAllCountries from "@hooks/countries/use-all-countries";
import { useStoreContext } from "@components/store/provider";

import { getCountry } from "@selectors/country";
import { getStadiumCatalogNumber } from "@selectors/config";

import Multimedia from "@sharedComponents/multimedia";
import { NewCountrySelector } from "@stadium/ui/src/components/country-selector";

import { ExternalButton } from "@sharedComponents/buttons";

import { OFFERINGS_PATH, getStoreBasePathWithoutCountryCode } from "@constants/routes";
import {
  GET_STARTED_CTA,
  OFFERINGS,
  OFFERINGS_FLOW_SERVICES,
  SWAG_CATALOG,
  SHOP_CATALOG_MODE,
} from "@constants/june-so";

import getStartedBannerImage from "@assets/images/get-started-banner-image.png";
import getStartedBannerBgImage from "@assets/images/get-started-bg-image.png";
import bulkSwagLargeImg from "@images/offerings/bulk-swag-large.png";

import styles from "./main.module.scss";

export const GiftingCatalogTopBanner = () => {
  const selectedCountry = useSelector(getCountry);
  const { lockerCatalogUrl } = useLockerUrlConfig();
  const { instance: Weglot, availableLanguages } = useContext(WeglotContext);
  const { changeLang, langInUrl } = useLanguageChange();
  const { allCountriesList } = useAllCountries();
  const { changeCountry } = useCountryChange(allCountriesList);
  const catalogShopNumber = useConfig(getStadiumCatalogNumber);
  const onSelectCountry = useCallback((country) => changeCountry(country), [changeCountry]);
  const { isNewRoute, workspaceSlug } = useStoreContext();

  const catalogFeaturePageUrl = useMemo(
    () =>
      getStoreBasePathWithoutCountryCode({
        storeCode: "catalog",
        storeNumber: catalogShopNumber,
        workspaceSlug,
        isNewRoute,
      }),
    [catalogShopNumber, workspaceSlug, isNewRoute],
  );

  return (
    <div className={styles.exploreCatalogBanner}>
      <div className={styles.catalogBannerContainer}>
        <div className={styles.bannerLeftSection}>
          <ExternalButton
            styleType="link"
            href={catalogFeaturePageUrl}
            className={styles.bannerHeaderBackLink}
            target=""
            analytics={{
              componentName: SHOP_CATALOG_MODE,
              target: "go-back-to-feature-page",
            }}
          >
            <span className={styles.backIcon}></span>GO BACK TO FEATURE PAGE
          </ExternalButton>
          <div className={styles.bannerLeftTitle}>
            Explore the most expansive gifting catalog out there
          </div>
          <div className={styles.bannerLeftDescription}>
            Transform your brand with our on-demand or bulk swag!
            <ExternalButton
              styleType="link"
              href={lockerCatalogUrl}
              target="_blank"
              showExternalLinkIcon
              className={styles.bannerDescriptionLink}
              analytics={{
                componentName: SWAG_CATALOG,
                target: "visit-swag-catalog",
              }}
            >
              <span className={styles.bannerDescriptionLinkText}>Visit Swag Catalog</span>
            </ExternalButton>
          </div>
        </div>

        <div className={styles.bannerRightSection}>
          <div className={styles.dropdownBlock}>
            <NewCountrySelector
              countries={allCountriesList}
              onSelectCountry={onSelectCountry}
              selectedCountry={selectedCountry}
              flagWidth={32}
              flagHeight={24}
              weglotInstance={Weglot}
              availableLanguages={availableLanguages}
              onSelectLang={changeLang}
              langInUrl={langInUrl}
              type="round"
              showCountryChangeModal={false}
              labelText="EXPLORE 170+ COUNTRIES"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export const GetStartedBanner = () => (
  <section className={styles.getStartedBanner}>
    <Multimedia
      type="image"
      src={getStartedBannerBgImage}
      width={85}
      height={92}
      alt="get-started-bg-img"
      className={styles.getStartedBannerBackgroundImage}
      isNextImage={false}
    />
    <div className={styles.bannerTextCol}>
      <div className={styles.bannerColTitle}>Like what you see?</div>
    </div>

    <div className={styles.bannerImageHolder}>
      <Multimedia
        type="image"
        src={getStartedBannerImage}
        width={85}
        height={92}
        alt="get-started-char-img"
      />
    </div>

    <div className={styles.bannerCtaCol}>
      <ExternalButton
        styleType="primary"
        href={OFFERINGS_PATH}
        analytics={{
          componentName: OFFERINGS,
          target: GET_STARTED_CTA,
          services: OFFERINGS_FLOW_SERVICES,
        }}
      >
        GET STARTED
      </ExternalButton>
    </div>
  </section>
);

export const SwagCatalogBanner = () => {
  const { lockerCatalogUrl } = useLockerUrlConfig();

  return (
    <section className={styles.onDemandSwagBanner}>
      <div className={styles.bannerContentSection}>
        <div className={styles.bannerContentHolder}>
          <div className={styles.bannerContentTitle}>On-demand and bulk swag like nowhere else</div>
          <div className={styles.bannerContentDescription}>
            Order only what you need so you can save money and waste -with no order minimums
            required. Why go anywhere else?
          </div>
          <ExternalButton
            styleType="primary"
            href={lockerCatalogUrl}
            showExternalLinkIcon
            className={styles.bannerContentButton}
            target="_blank"
            analytics={{
              componentName: SWAG_CATALOG,
              target: "browse-swag-catalog",
            }}
          >
            Browse Swag Catalog
          </ExternalButton>
        </div>
      </div>

      <div className={styles.bannerImageSection}>
        <Multimedia
          type="image"
          src={bulkSwagLargeImg}
          className={styles.bannerSectionImage}
          alt="get-started-char-img"
        />
      </div>
    </section>
  );
};
